import { FormTemplate, CodeStub, randomUuid } from '@icure/cardinal-sdk'
import { FormTemplateFilters } from '@icure/cardinal-sdk/filters.mjs'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { cardinalApi, guard } from '../services/auth.api'
import { Form } from '@icure/form/components/model'
import YAML from 'yaml'
import { PREVENTI_FORM_TEMPLATE_GUID, PREVENTI_FORM_TEMPLATE_SPECIALTY_CODE } from '../../constants'
import { getRawFormTemplates } from './formTemplateUtils'

function parseTimestampFromName(name: string | undefined): number {
  if (!name) return 0
  const match = name.match(/Preventi\s+(.+)/)
  if (!match) return 0
  const date = new Date(match[1])
  return isNaN(date.getTime()) ? 0 : date.getTime()
}

function sortTemplatesByDate(templates: FormTemplate[]): FormTemplate[] {
  return [...templates].sort((a, b) => parseTimestampFromName(b.name) - parseTimestampFromName(a.name))
}

export const formTemplateApiRtk = createApi({
  reducerPath: 'formTemplateApi',
  tagTypes: ['FormTemplate'],
  baseQuery: fetchBaseQuery({
    baseUrl: '',
  }),
  endpoints: (builder) => ({
    getFormTemplatesByGuid: builder.query<FormTemplate[] | undefined, void>({
      async queryFn(_arg, { getState }) {
        const formApi = (await cardinalApi(getState))?.form
        return guard([formApi], async ([formApi]): Promise<FormTemplate[]> => {
          const ids = await formApi.matchFormTemplateBy(FormTemplateFilters.bySpecialtyCode(PREVENTI_FORM_TEMPLATE_SPECIALTY_CODE))
          if (!ids.length) return []
          const templates = await getRawFormTemplates(formApi, ids)
          const filtered = templates.filter((t) => t.guid === PREVENTI_FORM_TEMPLATE_GUID)
          return sortTemplatesByDate(filtered)
        })
      },
      providesTags: [{ type: 'FormTemplate', id: 'all' }],
    }),
    getFormTemplateById: builder.query<FormTemplate | undefined, string>({
      async queryFn(templateId, { getState }) {
        const formApi = (await cardinalApi(getState))?.form
        return guard([formApi], async ([formApi]): Promise<FormTemplate | undefined> => {
          try {
            return await formApi.getFormTemplate(templateId)
          } catch {
            return undefined
          }
        })
      },
      providesTags: (result) => (result?.id ? [{ type: 'FormTemplate', id: result.id }] : []),
    }),
    getFormTemplateAttachment: builder.query<Form | undefined, string>({
      async queryFn(templateId, { getState }) {
        const formApi = (await cardinalApi(getState))?.form
        return guard([formApi], async ([formApi]): Promise<Form | undefined> => {
          try {
            const template = await formApi.getFormTemplate(templateId, { raw: true })
            if (!template.rawTemplateLayout) return undefined
            const text = new TextDecoder().decode(template.rawTemplateLayout)
            return Form.parse(YAML.parse(text))
          } catch {
            return undefined
          }
        })
      },
      providesTags: (_result, _error, templateId) => [{ type: 'FormTemplate', id: templateId }],
    }),
    getFormTemplateRawAttachment: builder.query<string | undefined, string>({
      async queryFn(templateId, { getState }) {
        const formApi = (await cardinalApi(getState))?.form
        return guard([formApi], async ([formApi]): Promise<string | undefined> => {
          try {
            const template = await formApi.getFormTemplate(templateId, { raw: true })
            if (!template.rawTemplateLayout) return undefined
            return new TextDecoder().decode(template.rawTemplateLayout)
          } catch {
            return undefined
          }
        })
      },
      providesTags: (_result, _error, templateId) => [{ type: 'FormTemplate', id: templateId }],
    }),
    createFormTemplateVersion: builder.mutation<FormTemplate | undefined, { yamlContent: string }>({
      async queryFn({ yamlContent }, { getState }) {
        const formApi = (await cardinalApi(getState))?.form
        return guard([formApi], async ([formApi]): Promise<FormTemplate | undefined> => {
          const template = new FormTemplate({
            id: randomUuid(),
            guid: PREVENTI_FORM_TEMPLATE_GUID,
            name: `Preventi ${new Date().toISOString()}`,
            specialty: new CodeStub({ code: PREVENTI_FORM_TEMPLATE_SPECIALTY_CODE }),
          })
          const created = await formApi.createFormTemplate(template)
          const encoded = new TextEncoder().encode(yamlContent)
          await formApi.setTemplateAttachment(created.id, new Int8Array(encoded.buffer))
          return created
        })
      },
      invalidatesTags: [{ type: 'FormTemplate', id: 'all' }],
    }),
    updateFormTemplateAttachment: builder.mutation<void | undefined, { templateId: string; yamlContent: string }>({
      async queryFn({ templateId, yamlContent }, { getState }) {
        const formApi = (await cardinalApi(getState))?.form
        return guard([formApi], async ([formApi]): Promise<void> => {
          const encoded = new TextEncoder().encode(yamlContent)
          await formApi.setTemplateAttachment(templateId, new Int8Array(encoded.buffer))
        })
      },
      invalidatesTags: (_result, _error, { templateId }) => [
        { type: 'FormTemplate', id: 'all' },
        { type: 'FormTemplate', id: templateId },
      ],
    }),
  }),
})

export const {
  useGetFormTemplatesByGuidQuery,
  useGetFormTemplateByIdQuery,
  useGetFormTemplateAttachmentQuery,
  useGetFormTemplateRawAttachmentQuery,
  useLazyGetFormTemplateRawAttachmentQuery,
  useCreateFormTemplateVersionMutation,
  useUpdateFormTemplateAttachmentMutation,
} = formTemplateApiRtk
